import React, { useRef, useState, useContext, useEffect } from "react";
import styles from "./styles/PosLogin.module.css";
import { useTranslation } from "react-i18next";
import Button from "../../../components/ui/button/Button";
import InputText from "../../../components/ui/input_text/InputText";
import DataContext from "../../../context/DataContext";
import { encryptor } from "../../../utils/utils";
import { axiosSW, axiosSWLogIn } from "../../../api/axiosDefaults";
import AuthContext from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import POSDataContext from "../../../context/POSDataContext";
import Modal from "./Modal";
import useAuthReady from "../../../hooks/useAuthReady";

/**
 * The LogIn component
 */

const LogInForm = () => {
    const { t } = useTranslation();
    const {
        connection, // {Object} - the customer settings
        homePath, // {String} - the home path
    } = useContext(DataContext);
    const {
        handleErrorPos, // {Function} - the error handler
        setPayments,
    } = useContext(POSDataContext);
    const {
        user, // {Object} - the authinticated user object
        login, // {Function} - the login function for user authentication
    } = useContext(AuthContext);
    const userNameRef = useRef(null);
    const [userData, setUserData] = useState({
        userName: "",
        userPassword: "",
    });
    const [hoverFlag, setHoverFlag] = useState(false);
    const [inProcess, setInProcess] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [formButtonDisable, setFormButtonDisable] = useState(true);
    const navigate = useNavigate();

    // Focus on the first input field
    useEffect(() => {
        userNameRef.current && userNameRef.current.focus();
    }, []);

    // Set form button disable state
    useEffect(() => {
        setFormButtonDisable(!(userData?.userName && userData?.userPassword));
    }, [userData]);

    /**
     * Reset the form data
     */
    const handleReset = () => {
        setSuccess(false);
        setUserData({
            userName: "",
            userPassword: "",
        });
        setHoverFlag(false);
    };

    /**
     * Change handler
     * @param {event} event - Form submit event
     */
    const handleChange = (event) => {
        setUserData((prevUserData) => ({
            ...prevUserData,
            [event.target.name]:
                event.target.type === "checkbox"
                    ? event.target.checked
                    : event.target.value,
        }));
    };

    /**
     * Submit handler
     * @param {event} event - Form submit event
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formButtonDisable) {
            setInProcess(true);
            const payload = {
                uniqueIdentifier: connection?.uniqueIdentifier,
                intKey: connection?.intKey,
                username: userData?.userName,
                password: encryptor(userData?.userPassword, connection?.intKey),
            };
            try {
                const { data } = await axiosSWLogIn.post(
                    "Authentication/pos/ValidateCashierCredentials",
                    payload,
                );

                // use the AuthContext login function to set the user
                login({
                    userName: userData?.userName,
                    userRole: data?.role,

                    cashierID: data?.cashierID,
                    cashierSessionID: data?.cashierSessionID,
                    cartID: data?.cartID,
                    cashierSessionDate: data?.cashierSessionDate,
                });
                setPayments([]);
                setSuccess(true);
                setInProcess(false);
                navigate(`${homePath}/tickets`);
            } catch (error) {
                if (error?.request?.status === 401) {
                    setErrorMessage(error?.response?.data?.statusMessage);

                    setInProcess(false);
                    setSuccess(true);
                } else {
                    console.log(error);
                    setInProcess(false);
                    handleErrorPos(error);
                }
            }
        }
    };
    return (
        <>
            {/* if the data was fetched successfully and the user is not authenticated 
            show the the Modal. The feed back window with the error message from the server red colored 
            or if in process show a spinner in the window with the current theme color */}
            {(success && !user) || inProcess ? (
                <Modal
                    inProcess={inProcess}
                    errorMessage={errorMessage}
                    handleReset={handleReset}
                />
            ) : (
                // if the user is not authenticated show the login form
                !user && (
                    <div
                        className={`${styles.main_form_container} v-flex-container`}
                    >
                        <div
                            className={`${styles.form_header_container} flex-container`}
                        >
                            <h1 className={`${styles.form_header}`}>
                                {t(`login`)}
                            </h1>
                        </div>
                        <form
                            className={`${styles.form_container} v-flex-container`}
                            onSubmit={handleSubmit}
                            noValidate={true}
                        >
                            <div className={`${styles.form_inputs_container}`}>
                                <InputText
                                    ref={userNameRef}
                                    letterSpacing={".2rem"}
                                    placeholder={t(`required field`)}
                                    label={t(`user name`)}
                                    inputId="userName"
                                    inputValue={userData?.userName}
                                    handleChangeFunction={handleChange}
                                    required={hoverFlag && !userData?.userName}
                                />
                                <InputText
                                    type={"password"}
                                    letterSpacing={".2rem"}
                                    placeholder={t(`required field`)}
                                    label={t(`password`)}
                                    inputId="userPassword"
                                    inputValue={userData?.userPassword}
                                    handleChangeFunction={handleChange}
                                    required={
                                        hoverFlag && !userData?.userPassword
                                    }
                                />
                            </div>
                            <Button
                                body={t(`submit`)}
                                type={"submit"}
                                disabled={formButtonDisable}
                                setHoverFlag={setHoverFlag}
                            />
                        </form>
                    </div>
                )
            )}
        </>
    );
};

export default LogInForm;
